// V4-FRONT-ANY-USAGE batch 4 2026-05-28
/**
 * V4-ZERNIO-COMMENTS — Onglet Engagement (inbox commentaires).
 *
 * Liste les commentaires non répondus du space courant, avec filtres par network
 * et statut. Permet de répondre inline, hide ou like.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, RefreshCw, Loader2, Send, EyeOff, Heart, Check,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { TabHeader } from '../../common/TabHeader';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import { toastService } from '../../../services/toastService';
import { Logger } from '../../../utils/logger';
import { getChannelDisplayConfig } from '../../../utils/Constants';
import { formatDateTime } from '../../../utils/dateUtils';
import {
  zernioCommentsService,
  type InboxComment,
  type CommentStatusFilter,
} from '../../../services/zernioCommentsService';

interface EngagementTabProps {
  onClose: () => void;
}

// V4-ZERNIO-COMMENTS i18n 2026-05-28 — labels via t.engagementTab.statusXxx
// (résolus dans le composant, pas en const top-level statique).

// Labels réseau : source de vérité = `getChannelDisplayConfig` (utils/Constants).
// Date format : source de vérité = `formatDateTime` (utils/dateUtils).

function CommentCard({
  comment,
  onReply,
  onHide,
  onLike,
}: {
  comment: InboxComment;
  onReply: (id: string, content: string) => Promise<void>;
  onHide: (id: string) => Promise<void>;
  onLike: (id: string) => Promise<void>;
}) {
  const { t } = useLanguage();
  const te = t.engagementTab;
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      await onReply(comment.comment_id, replyText.trim());
      setReplyOpen(false);
      setReplyText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-2"
      style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div className="flex items-start gap-3">
        {comment.author_avatar_url ? (
          <img
            src={comment.author_avatar_url}
            alt={comment.author_name ?? 'avatar'}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div
            className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center"
            style={{
              backgroundColor: 'var(--color-primary-light, rgba(124, 58, 237, 0.15))',
              color: 'var(--color-primary)',
            }}
          >
            {(comment.author_name ?? '?').slice(0, 1).toUpperCase()}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
              {comment.author_name ?? te.anonymous}
            </span>
            {comment.channel_network && (
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {getChannelDisplayConfig(comment.channel_network).label}
              </span>
            )}
            {comment.replied && (
              <span
                className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                style={{
                  backgroundColor: 'var(--color-success-light, rgba(34, 197, 94, 0.12))',
                  color: 'var(--color-success, #22c55e)',
                }}
              >
                <Check className="w-3 h-3" /> {te.replied}
              </span>
            )}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            {comment.created_at ? formatDateTime(comment.created_at) : ''}
          </div>
          <p className="mt-2 text-sm whitespace-pre-wrap" style={{ color: 'var(--color-text-primary)' }}>
            {comment.content}
          </p>

          {comment.replied && comment.reply_content && (
            <div
              className="mt-2 p-2 rounded-lg text-sm"
              style={{
                backgroundColor: 'var(--color-background)',
                borderLeft: '3px solid var(--color-primary)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {te.yourReply}
              </span>
              <div>{comment.reply_content}</div>
            </div>
          )}

          {/* Reply box */}
          {replyOpen && !comment.replied && (
            <div className="mt-3 flex flex-col gap-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={te.replyPlaceholder}
                rows={3}
                className="w-full rounded-lg p-2 text-sm resize-none"
                style={{
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setReplyOpen(false);
                    setReplyText('');
                  }}
                  disabled={submitting}
                >
                  {te.cancel}
                </Button>
                <Button size="sm" onClick={handleSend} disabled={submitting || !replyText.trim()}>
                  {submitting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1" />
                      {te.send}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-1">
        {!comment.replied && !replyOpen && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReplyOpen(true)}
            className="rounded-full"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {te.reply}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onLike(comment.comment_id)}
          className="rounded-full"
          disabled={comment.liked}
          title={comment.liked ? te.alreadyLiked : te.like}
        >
          <Heart
            className="w-3.5 h-3.5 mr-1.5"
            style={comment.liked ? { fill: 'var(--color-primary)', color: 'var(--color-primary)' } : undefined}
          />
          {te.like}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onHide(comment.comment_id)}
          className="rounded-full"
          disabled={comment.hidden}
          title={comment.hidden ? te.alreadyHidden : te.hide}
        >
          <EyeOff className="w-3.5 h-3.5 mr-1.5" />
          {te.hide}
        </Button>
      </div>
    </div>
  );
}

export function EngagementTab({ onClose }: EngagementTabProps) {
  const { t } = useLanguage();
  const te = t.engagementTab;
  const { currentSpace } = useAuth();
  const spaceId = currentSpace?.id;

  const [status, setStatus] = useState<CommentStatusFilter>('unreplied');
  const [comments, setComments] = useState<InboxComment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // V4-ZERNIO-COMMENTS i18n 2026-05-28 — labels filtres résolus via t
  const statusOptions: { value: CommentStatusFilter; label: string }[] = [
    { value: 'unreplied', label: te.statusUnreplied },
    { value: 'replied', label: te.statusReplied },
    { value: 'all', label: te.statusAll },
  ];

  const load = useCallback(async () => {
    if (!spaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await zernioCommentsService.list(spaceId, { status, limit: 50 });
      setComments(res.items);
      setTotal(res.total);
    } catch (err) {
      Logger.error('[EngagementTab] Failed to load comments', err);
      setError(te.loadError);
      toastService.error(te.loadError);
    } finally {
      setLoading(false);
    }
  }, [spaceId, status, te.loadError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReply = async (commentId: string, content: string) => {
    try {
      await zernioCommentsService.reply(commentId, content);
      toastService.success(te.replySuccess);
      // Optimistic update: mark as replied
      setComments((prev) =>
        prev.map((c) =>
          c.comment_id === commentId
            ? { ...c, replied: true, reply_content: content, replied_at: new Date().toISOString() }
            : c,
        ),
      );
      // Refresh from server (cron will also catch up)
      setTimeout(load, 500);
    } catch (err) {
      Logger.error('[EngagementTab] reply failed', err);
      toastService.error(te.replyFailed);
      throw err;
    }
  };

  const handleHide = async (commentId: string) => {
    try {
      await zernioCommentsService.hide(commentId);
      toastService.success(te.hideSuccess);
      setComments((prev) =>
        prev.map((c) => (c.comment_id === commentId ? { ...c, hidden: true } : c)),
      );
    } catch (err) {
      Logger.error('[EngagementTab] hide failed', err);
      toastService.error(te.hideFailed);
    }
  };

  const handleLike = async (commentId: string) => {
    try {
      await zernioCommentsService.like(commentId);
      setComments((prev) =>
        prev.map((c) => (c.comment_id === commentId ? { ...c, liked: true } : c)),
      );
    } catch (err) {
      Logger.error('[EngagementTab] like failed', err);
      toastService.error(te.likeFailed);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header — coque partagée V4-FRONT-HEADERS-UNIFORM */}
      <TabHeader
        icon={<MessageSquare className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />}
        title={t.menu.engagement}
        count={total > 0 ? total : undefined}
        actions={
          <Button
            variant="ghost"
            size="icon"
            onClick={load}
            disabled={loading}
            title={te.refresh}
            className="rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        }
        onClose={onClose}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Filter tabs */}
        <div className="flex items-center gap-2">
          {statusOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatus(opt.value)}
              className="px-3 py-1 rounded-full text-sm transition-colors"
              style={{
                backgroundColor:
                  opt.value === status
                    ? 'var(--color-primary)'
                    : 'var(--color-surface)',
                color:
                  opt.value === status
                    ? 'var(--color-text-light)'
                    : 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {!spaceId && (
          <div className="text-center py-12" style={{ color: 'var(--color-text-muted)' }}>
            {te.noSpace}
          </div>
        )}

        {error && (
          <div
            className="rounded-lg p-3 text-sm"
            style={{
              backgroundColor: 'var(--color-danger-light, rgba(239, 68, 68, 0.1))',
              border: '1px solid var(--color-danger, #ef4444)',
              color: 'var(--color-danger, #ef4444)',
            }}
          >
            {error}
          </div>
        )}

        {loading && comments.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} />
          </div>
        )}

        {!loading && comments.length === 0 && !error && spaceId && (
          <div
            className="rounded-lg p-6 text-sm text-center"
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {status === 'unreplied'
              ? te.emptyUnreplied
              : te.emptyOther}
          </div>
        )}

        {comments.map((c) => (
          <CommentCard
            key={c.comment_id}
            comment={c}
            onReply={handleReply}
            onHide={handleHide}
            onLike={handleLike}
          />
        ))}
      </div>
    </div>
  );
}
